import type { FacilityDetails } from '@gyym/sdk'

import { getImageUrl } from '@/utils/getImageUrl'

import { DEFAULT_PRICE_RANGE, GYYM_ORGANIZATION } from '../constants'
import { safeJoin, sanitizeString } from '../sanitize'
import type { ExerciseGym, ImageObject, PostalAddress } from '../types'
import { buildAmenityFeature } from './buildAmenityFeature'
import { buildOfferCatalog } from './buildOfferCatalog'
import { buildReviews } from './buildReviews'

interface BuildExerciseGymParams {
  facility: FacilityDetails
  prefecture: { name: string; slug: string }
  city: { code: number; name: string }
  canonicalUrl: string
}

/**
 * Build ExerciseGym structured data for facility
 */
export const buildExerciseGym = ({
  facility,
  prefecture,
  city,
  canonicalUrl,
}: BuildExerciseGymParams): ExerciseGym => {
  // Build images array with absolute URLs
  const images: ImageObject[] = facility.images.map((url, index) => ({
    '@type': 'ImageObject',
    url: getImageUrl(url),
    name: index === 0 ? facility.name : `${facility.name} - ${index + 1}`,
  }))

  // Build address
  const address: PostalAddress = {
    '@type': 'PostalAddress',
    addressCountry: 'JP',
    addressRegion: prefecture.name,
    addressLocality: city.name,
    name: sanitizeString(facility.address),
  }

  // Build description from facility description and attention
  const attentionText =
    facility.attention && facility.attention.length > 0
      ? facility.attention.join(' ')
      : ''
  const description = sanitizeString(
    safeJoin([facility.description, attentionText], ' '),
  )

  // Build aggregate rating
  const aggregateRating =
    facility.reviewScore > 0 && facility.reviewCount > 0
      ? {
          '@type': 'AggregateRating' as const,
          ratingValue: facility.reviewScore,
          ratingCount: facility.reviewCount,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined

  // Build amenity features
  const amenityFeature = buildAmenityFeature(facility.amenities)

  // Build reviews
  const reviews =
    facility.reviews && facility.reviews.length > 0
      ? buildReviews(facility.reviews)
      : undefined

  // Build offer catalog
  const hasOfferCatalog = buildOfferCatalog(facility.programs)

  const exerciseGym: ExerciseGym = {
    '@type': 'ExerciseGym',
    '@id': canonicalUrl,
    name: sanitizeString(facility.name),
    url: canonicalUrl,
    description,
    image: images,
    address,
    geo:
      facility.latitude && facility.longitude
        ? {
            '@type': 'GeoCoordinates',
            latitude: facility.latitude,
            longitude: facility.longitude,
          }
        : undefined,
    priceRange: DEFAULT_PRICE_RANGE,
    priceCurrency: 'JPY',
    publisher: GYYM_ORGANIZATION,
    amenityFeature: amenityFeature.length > 0 ? amenityFeature : undefined,
    aggregateRating,
    review: reviews,
    hasOfferCatalog: hasOfferCatalog ?? undefined,
    isAccessibleForFree: false,
    publicAccess: true,
  }

  // Add telephone if available
  if (facility.tel) {
    exerciseGym.telephone = facility.tel
  }

  // Add homepage as sameAs if available
  if (facility.homepage) {
    exerciseGym.sameAs = facility.homepage
  }

  return exerciseGym
}
